import { useState } from "react";
import CaregiverUploadField from "../caregiver/CaregiverUploadField";
import { uploadFileToS3 } from "../../services/uploadService";
import api from "../../services/api";
import { showToast } from "../../utils/toast";
import { sanitizeAadhaarDigits, isValidAadhaarNumber } from "../../utils/indianIdInput";
import { formatKycStatus } from "../../utils/displayLabels";
import { shouldShowCustomerAadhaarKycUi } from "../../config/customerAccessPolicy";

export function isCustomerKycVerified(status) {
  const s = String(status || "").toUpperCase();
  return s === "VERIFIED" || s === "APPROVED" || s === "COMPLETE";
}

export function isCustomerKycSubmitted(status, kycStatus = {}) {
  const s = String(status || kycStatus?.kycStatus || "").toUpperCase();
  if (s === "SUBMITTED" || s === "IN_REVIEW") return true;
  return Boolean(
    kycStatus?.submittedAt &&
      kycStatus?.documents?.frontUploaded &&
      kycStatus?.documents?.backUploaded &&
      !isCustomerKycVerified(s)
  );
}

export default function CustomerKycSection({ kycStatus, onStatusChange }) {
  const [submitting, setSubmitting] = useState(false);
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [files, setFiles] = useState({ front: null, back: null, selfie: null });
  const [error, setError] = useState("");

  // B2C: hide. Component + Ops approve APIs stay for future B2B (flag on).
  if (!shouldShowCustomerAadhaarKycUi()) return null;

  const verified = Boolean(kycStatus?.verified);
  const submitted = !verified && isCustomerKycSubmitted(kycStatus?.kycStatus, kycStatus);
  const statusLabel = verified ? "Verified" : submitted ? "Submitted — under review" : formatKycStatus(kycStatus?.kycStatus);
  const hasStoredAadhaar =
    kycStatus?.hasAadhaarNumber === true ||
    (kycStatus?.hasAadhaarNumber == null && Boolean(kycStatus?.aadhaarMasked));

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const normalized = sanitizeAadhaarDigits(aadhaarNumber);

    if (!isValidAadhaarNumber(normalized)) {
      setError("Enter your 12-digit Aadhaar number.");
      return;
    }
    if (!files.front || !files.back) {
      setError("Upload Aadhaar front and back images to continue.");
      return;
    }

    setSubmitting(true);
    try {
      const [frontAsset, backAsset, selfieAsset] = await Promise.all([
        uploadFileToS3(files.front, { folder: "customer/kyc/front" }),
        uploadFileToS3(files.back, { folder: "customer/kyc/back" }),
        files.selfie ? uploadFileToS3(files.selfie, { folder: "customer/kyc/selfie" }) : Promise.resolve(null),
      ]);

      const res = await api.submitCustomerKyc({
        aadhaarNumber: normalized,
        frontUploaded: true,
        backUploaded: true,
        selfieUploaded: Boolean(selfieAsset),
        frontFileName: frontAsset?.fileName || files.front?.name,
        frontFileUrl: frontAsset?.url || "",
        frontStorageKey: frontAsset?.storageKey || frontAsset?.key || "",
        backFileName: backAsset?.fileName || files.back?.name,
        backFileUrl: backAsset?.url || "",
        backStorageKey: backAsset?.storageKey || backAsset?.key || "",
        selfieFileName: selfieAsset?.fileName || files.selfie?.name || "",
        selfieFileUrl: selfieAsset?.url || "",
        selfieStorageKey: selfieAsset?.storageKey || selfieAsset?.key || "",
      });

      const kyc = res?.kyc || res;
      onStatusChange?.(kyc);
      if (isCustomerKycVerified(kyc?.kycStatus)) {
        showToast.success("Aadhaar KYC verified. Activate your subscription to book caregivers.");
      } else {
        showToast.info("KYC submitted. Admin will review your documents — you will be notified once approved.");
      }
      setFiles({ front: null, back: null, selfie: null });
    } catch (err) {
      setError(err.message || "Unable to submit KYC.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="aadhaar-kyc" className="customer-profile__kyc" aria-labelledby="customer-kyc-heading">
      <div className="customer-profile__kyc-head">
        <h3 id="customer-kyc-heading">Aadhaar KYC</h3>
        <span
          className={`customer-profile__kyc-pill ${
            verified
              ? "customer-profile__kyc-pill--ok"
              : submitted
                ? "customer-profile__kyc-pill--review"
                : "customer-profile__kyc-pill--pending"
          }`}
        >
          {verified ? "Verified" : statusLabel}
        </span>
      </div>
      <p className="customer-profile__kyc-lede">
        Upload your Aadhaar documents here. An admin will review and approve before you can book caregivers.
        {kycStatus?.phoneVerified ? " Your mobile number is verified." : ""}
      </p>

      {verified ? (
        <p className="customer-profile__kyc-done">Your identity is verified. No further action needed.</p>
      ) : submitted ? (
        <p className="customer-profile__kyc-done">
          Your Aadhaar documents are submitted and under review. You will be notified once approved.
        </p>
      ) : (
        <form
          className="customer-profile__form customer-profile__kyc-form"
          onSubmit={handleSubmit}
          noValidate
        >
          {hasStoredAadhaar && kycStatus?.aadhaarMasked ? (
            <p className="customer-profile__kyc-lede">
              Aadhaar on file: <strong>{kycStatus.aadhaarMasked}</strong>. Re-enter below only if you need to
              update it.
            </p>
          ) : null}
          <label className="customer-profile__field--full">
            <strong>Aadhaar number <span className="required-star">*</span></strong> 
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={12}
              value={aadhaarNumber}
              onChange={(e) => setAadhaarNumber(sanitizeAadhaarDigits(e.target.value))}
              placeholder="12-digit Aadhaar"
            />
          </label>

          <div className="customer-profile__field--full">
            <CaregiverUploadField
              inputId="customer-profile-kyc-front"
              title="Aadhaar — front"
              required
              file={files.front}
              onChange={(file) => setFiles((prev) => ({ ...prev, front: file }))}
              accept="image/*,.pdf"
              enableCamera
            />
          </div>
          <div className="customer-profile__field--full">
            <CaregiverUploadField
              inputId="customer-profile-kyc-back"
              title="Aadhaar — back"
              required
              file={files.back}
              onChange={(file) => setFiles((prev) => ({ ...prev, back: file }))}
              accept="image/*,.pdf"
              enableCamera
            />
          </div>
          <div className="customer-profile__field--full">
            <CaregiverUploadField
              inputId="customer-profile-kyc-selfie"
              title="Selfie with Aadhaar (optional)"
              file={files.selfie}
              onChange={(file) => setFiles((prev) => ({ ...prev, selfie: file }))}
              accept="image/*"
              enableCamera
              cameraFacing="user"
            />
          </div>

          {error ? (
            <p className="customer-profile__error customer-profile__field--full">{error}</p>
          ) : null}

          <button
            type="submit"
            className="customer-profile__save customer-profile__field--full"
            disabled={submitting}
          >
            {submitting ? "Submitting KYC…" : "Submit Aadhaar KYC"}
          </button>
        </form>
      )}
    </section>
  );
}
